import { choiceChips } from './choice-filters.js';
import { RecordCollection } from './record-collection.js';
import {
  DateRangePicker,
  formatCivilRange,
  StatusPill,
  todayCivilDate,
  type StatusTone,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock3, MailCheck, Send } from 'lucide-react';
import {
  getApiError,
  outboxApi,
  type EmailMessage,
  type EmailMessageSort,
  type EmailMessageStatus,
  type EmailMessagePage,
} from '../lib/api.js';
import { formatDateTime } from '../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  MultiFilter,
  LoadingCard,
  Pagination,
  SortBar,
} from './data-view.js';
import type { OutboxView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { PageHeader } from './page-title.js';

const statusOptions: Array<{ value: EmailMessageStatus; label: string }> = [
  { value: 'PENDING', label: 'A caminho' },
  { value: 'SENT', label: 'Entregue' },
  { value: 'FAILED', label: 'Não saiu' },
];

const sortColumns: Array<{ sort: EmailMessageSort; label: string }> = [
  { sort: 'createdAt', label: 'Quando' },
  { sort: 'recipient', label: 'Para quem' },
  { sort: 'status', label: 'Situação' },
];

/// A situação dita em português, sem falar de fila nem de tentativa quando não precisa.
const statusLabels: Record<EmailMessageStatus, { label: string; tone: StatusTone }> = {
  PENDING: { label: 'A caminho', tone: 'progress' },
  SENT: { label: 'Entregue ao provedor', tone: 'positive' },
  FAILED: { label: 'Não saiu', tone: 'danger' },
};

/// A caixa de saída de e-mails.
///
/// Existe para responder à pergunta que aparece assim que o sistema passa a mandar e-mail: "a
/// pessoa recebeu?". Sem ela, um convite que não chega é indistinguível de um convite que nunca foi
/// mandado, e a única saída seria olhar o banco.
///
/// `scope` decide de onde a lista vem: dentro da empresa, só o que é dela; no console da
/// plataforma, a fila inteira. O resto da tela é igual nos dois, porque a pergunta é a mesma.
export function CompanyOutbox({
  timeZone,
  scope = 'tenant',
  view,
  onView,
}: {
  timeZone: string;
  scope?: 'tenant' | 'platform';
  view: OutboxView;
  onView: ViewSetter<OutboxView>;
}) {
  const { search, status, event, from, to, sort, direction, limit, offset } = view;

  const filters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(status.length ? { status } : {}),
    ...(event.length ? { event } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    sort,
    direction,
    limit,
    offset,
  };
  const page = useQuery<EmailMessagePage>({
    queryKey: ['outbox', scope, filters],
    queryFn: () =>
      scope === 'platform' ? outboxApi.platformMessages(filters) : outboxApi.messages(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  function toggleSort(column: EmailMessageSort) {
    if (sort === column) onView({ direction: direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  const total = page.data?.total ?? 0;
  const messages = page.data?.messages ?? [];
  const eventOptions = page.data?.events ?? [];
  const activeFilters = [
    ...choiceChips('status', status, statusOptions, (status) => onView({ status })),
    ...choiceChips('event', event, eventOptions, (event) => onView({ event })),
    ...((from ?? to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from, to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  function clearFilters() {
    onView({ status: [], event: [], from: null, to: null });
  }

  return (
    <section aria-labelledby="company-outbox-title">
      {/* No console global o caminho é Comunicação, que é o item do menu e o guarda-chuva dos modelos de e-mail quando eles existirem. Dentro da empresa a caixa de saída ainda é item próprio em Configurações. */}
      <PageHeader
        trail={scope === 'platform' ? 'Plataforma / Comunicação' : 'Comunicação / Caixa de saída'}
        id="company-outbox-title"
        title="Caixa de saída"
        description="Todo e-mail que o sistema mandou: para quem foi, quando saiu e, quando não saiu, o que impediu."
      />

      <FilterBar
        searchId="outbox-search"
        searchLabel="Buscar e-mail"
        searchPlaceholder="Nome, e-mail ou assunto"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={clearFilters}
        count={`${String(total)} ${total === 1 ? 'e-mail' : 'e-mails'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'e-mail' : 'e-mails'}`}
        pageSize={limit}
        onPageSizeChange={(size) => onView({ limit: size })}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas as situações"
          options={statusOptions}
          value={status}
          onChange={(value) => onView({ status: value })}
        />
        <MultiFilter
          label="Tipo"
          itemNoun="tipos"
          allLabel="Todos os tipos"
          options={eventOptions}
          value={event}
          onChange={(event) => onView({ event })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período da caixa"
            value={{ from, to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando caixa de saída" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : messages.length ? (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <SortBar
            columns={sortColumns}
            sort={sort}
            direction={direction}
            onToggle={toggleSort}
            label="Ordenar os e-mails"
          />
          <RecordCollection
            label="E-mails enviados"
            records={messages}
            recordKey={(message) => message.id}
            sorting={{
              sort,
              direction,
              columns: sortColumns.map((item) => item.sort),
              onToggle: (key) => {
                const column = sortColumns.find((item) => item.sort === key);
                if (column) toggleSort(column.sort);
              },
            }}
            columns={[
              {
                key: 'subject',
                label: 'Assunto e tipo',
                render: (message) => (
                  <div>
                    <strong>{message.subject ?? message.eventLabel}</strong>
                    <p className="text-xs text-content-muted">{message.eventLabel}</p>
                  </div>
                ),
              },
              {
                key: 'recipient',
                label: 'Destinatário',
                render: (message) => (
                  <span className="text-xs text-content-muted">
                    {message.recipientName} · {message.recipientEmail}
                  </span>
                ),
              },
              {
                key: 'template',
                label: 'Texto',
                render: (message) => (
                  <span className="text-xs">
                    {message.templateOrigin === 'TENANT'
                      ? 'Modelo da empresa'
                      : 'Modelo da plataforma'}{' '}
                    · versão {message.templateVersion}
                  </span>
                ),
              },
              {
                key: 'createdAt',
                label: 'Datas',
                render: (message) => (
                  <div className="text-xs">
                    <p>
                      Pedido em <span>{formatDateTime(message.createdAt, timeZone)}</span>
                    </p>
                    {message.sentAt ? (
                      <p>
                        Saiu em <span>{formatDateTime(message.sentAt, timeZone)}</span>
                      </p>
                    ) : null}
                    {message.nextAttemptAt ? (
                      <p>
                        Tenta de novo {formatDateTime(message.nextAttemptAt, timeZone)} · restam{' '}
                        {message.remainingAttempts}{' '}
                        {message.remainingAttempts === 1 ? 'tentativa' : 'tentativas'}
                      </p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'status',
                label: 'Situação',
                render: (message) => (
                  <StatusPill tone={statusLabels[message.status].tone}>
                    {statusLabels[message.status].label}
                  </StatusPill>
                ),
              },
            ]}
            details={(message) =>
              message.status !== 'SENT' && message.lastError ? (
                <p className="rounded-xl border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  <strong>
                    Tentativa {message.attempts}
                    {message.status === 'FAILED' ? ' (última)' : ''}:{' '}
                  </strong>
                  {message.lastError}
                </p>
              ) : null
            }
            card={(message) => (
              <ol>
                <OutboxRow message={message} timeZone={timeZone} />
              </ol>
            )}
          />
          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onOffsetChange={(next) => onView({ offset: next })}
            itemLabel={(count) => (count === 1 ? 'e-mail' : 'e-mails')}
          />
        </div>
      ) : (
        <EmptyCard
          icon={MailCheck}
          title="Nenhum e-mail encontrado"
          description="Ajuste a busca, a situação, o tipo ou o período consultado."
        />
      )}
    </section>
  );
}

function OutboxRow({ message, timeZone }: { message: EmailMessage; timeZone: string }) {
  const situation = statusLabels[message.status];
  return (
    <li className="px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{message.subject ?? message.eventLabel}</p>
          <p className="mt-0.5 truncate text-sm text-content-muted">
            {message.recipientName} · {message.recipientEmail}
          </p>
        </div>
        <StatusPill
          tone={situation.tone}
          icon={
            message.status === 'SENT' ? (
              <Send className="size-3.5" />
            ) : message.status === 'PENDING' ? (
              <Clock3 className="size-3.5" />
            ) : (
              <AlertTriangle className="size-3.5" />
            )
          }
        >
          {situation.label}
        </StatusPill>
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs text-content-muted sm:grid-cols-2">
        <div className="flex gap-1.5">
          <dt className="text-content-faint">Tipo</dt>
          <dd className="min-w-0 truncate">{message.eventLabel}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="text-content-faint">Pedido em</dt>
          <dd>{formatDateTime(message.createdAt, timeZone)}</dd>
        </div>
        {/* Qual texto saiu, e não só qual número: a versão 2 da empresa e a 2 da plataforma são
            mensagens diferentes, e o número sozinho não identificaria nenhuma das duas. */}
        <div className="flex gap-1.5">
          <dt className="text-content-faint">Texto</dt>
          <dd className="min-w-0 truncate">
            {message.templateOrigin === 'TENANT' ? 'Modelo da empresa' : 'Modelo da plataforma'} ·
            versão {message.templateVersion}
          </dd>
        </div>
        {message.sentAt ? (
          <div className="flex gap-1.5">
            <dt className="text-content-faint">Saiu em</dt>
            <dd>{formatDateTime(message.sentAt, timeZone)}</dd>
          </div>
        ) : null}
        {message.nextAttemptAt ? (
          <div className="flex gap-1.5">
            <dt className="text-content-faint">Tenta de novo</dt>
            <dd>
              {formatDateTime(message.nextAttemptAt, timeZone)} · restam{' '}
              {message.remainingAttempts === 1
                ? '1 tentativa'
                : `${String(message.remainingAttempts)} tentativas`}
            </dd>
          </div>
        ) : null}
      </dl>

      {/* O motivo da falha aparece inteiro, e não escondido atrás de "ver detalhes": quem abre esta
          tela depois de um e-mail não chegar veio exatamente atrás dele. */}
      {message.status !== 'SENT' && message.lastError ? (
        <p className="mt-3 rounded-xl border border-danger/30 bg-danger/10 px-3 py-2 text-xs leading-5 text-danger">
          <span className="font-semibold">
            Tentativa {message.attempts}
            {message.status === 'FAILED' ? ' (última)' : ''}:
          </span>{' '}
          <span>{message.lastError}</span>
        </p>
      ) : null}
    </li>
  );
}
